import { useEffect, useState } from 'react';

declare global {
    interface Window {
        Kakao: any;
    }
}

const KakaoLoginButton = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (window.Kakao) {
            if (!window.Kakao.isInitialized()) {
                window.Kakao.init(import.meta.env.VITE_KAKAO_JS_KEY);
            }
            setIsLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js';
        script.crossOrigin = 'anonymous';
        script.async = true;

        script.onload = () => {
            window.Kakao.init(import.meta.env.VITE_KAKAO_JS_KEY);
            setIsLoaded(true);
        };

        document.head.appendChild(script);
    }, []);

    const handleKakaoLogin = () => {
        if (!isLoaded || !window.Kakao) return;

        /*
         * 카카오싱크 간편가입 동의화면을 띄우려면 serviceTerms가 필수
         *
         * ✅ 카카오 디벨로퍼스 설정 체크리스트:
         *   1. 내 애플리케이션 → 카카오 로그인 → 간편가입 → 활성화 ON
         *   2. 서비스 약관 탭 → 약관 등록 → 각 약관의 "태그" 값 확인
         *   3. .env 에 VITE_KAKAO_SERVICE_TERMS=태그1,태그2 형태로 입력
         *   4. 카카오싱크는 사전 검수 전 팀원 계정에서만 테스트 가능
         *
         * ⚠️ serviceTerms가 비어있거나 태그가 틀리면 일반 동의화면으로 fallback됨
         */
        const serviceTerms = import.meta.env.VITE_KAKAO_SERVICE_TERMS;

        // 개발 환경에서 serviceTerms 값 확인용 로그
        if (import.meta.env.DEV) {
            console.log(
                '[KakaoSync] serviceTerms:',
                serviceTerms || '⚠️ 비어있음 - .env 설정 필요',
            );
        }

        const authorizeParams: Record<string, string> = {
            redirectUri: import.meta.env.VITE_KAKAO_REDIRECT_URI,
            throughTalk: false as unknown as string,
        };

        /*
         * serviceTerms가 있을 때만 파라미터에 포함
         * 빈 문자열로 넘기면 카카오가 무시하고 일반 로그인 화면을 띄움
         */
        if (serviceTerms) {
            authorizeParams.serviceTerms = serviceTerms;
        }

        window.Kakao.Auth.authorize(authorizeParams);
    };

    return (
        <button
            onClick={handleKakaoLogin}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            disabled={!isLoaded}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                height: '45px',
                backgroundColor: isHovered ? '#E6CF00' : '#FEE500',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoaded ? 'pointer' : 'not-allowed',
                color: 'rgba(0,0,0,0.85)',
                fontSize: '14px',
                fontWeight: '700',
                letterSpacing: '-0.3px',
                padding: '0 16px',
                transition: 'background-color 0.15s ease',
                userSelect: 'none',
                opacity: isLoaded ? 1 : 0.6,
            }}
            aria-label="카카오로 시작하기"
        >
            <svg
                role="img"
                aria-label="Kakao"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="rgba(0,0,0,0.85)"
                style={{ flexShrink: 0 }}
            >
                <path d="M12 3C6.477 3 2 6.477 2 10.8c0 2.73 1.697 5.12 4.27 6.54l-1.088 3.97a.375.375 0 0 0 .55.415L10.14 19.4A11.4 11.4 0 0 0 12 19.6c5.523 0 10-3.477 10-7.8S17.523 3 12 3z" />
            </svg>
            <span>카카오로 시작하기</span>
        </button>
    );
};

export default KakaoLoginButton;
